
import { UserRole } from '@/types/user';

export interface RoleData {
  id: UserRole;
  title: string;
  description: string;
  features: string[];
  accountTier: 'free' | 'paid';
}

export const roles: RoleData[] = [
  {
    id: 'player',
    title: 'Player',
    description: 'Join teams, view schedules, and connect with teammates.',
    accountTier: 'free',
    features: [
      'Join teams and leagues',
      'View personal schedule',
      'Track statistics',
      'Connect with teammates'
    ]
  },
  {
    id: 'coach',
    title: 'Coach',
    description: 'Manage teams, set lineups and schedules.',
    accountTier: 'paid',
    features: [
      'Manage team roster',
      'Create lineups',
      'Schedule practices & games',
      'Team communication',
      'Advanced analytics'
    ]
  },
  {
    id: 'team-owner',
    title: 'Team Owner',
    description: 'Own/manage team assets and hiring.',
    accountTier: 'paid',
    features: [
      'Manage team operations',
      'Handle finances',
      'Recruit coaches & staff',
      'Team branding',
      'Advanced team tools'
    ]
  },
  {
    id: 'venue',
    title: 'Venue',
    description: 'Create listings and manage bookings.',
    accountTier: 'paid',
    features: [
      'List your venue',
      'Manage availability',
      'Handle bookings',
      'Promote facilities',
      'Revenue tracking'
    ]
  },
  {
    id: 'creative',
    title: 'Creative',
    description: 'Upload and tag sports content.',
    accountTier: 'free',
    features: [
      'Upload photos & videos',
      'Tag teams and players',
      'Build your portfolio',
      'Get discovered'
    ]
  },
  {
    id: 'support',
    title: 'Support',
    description: 'Provide essential game support and score keeping.',
    accountTier: 'free',
    features: [
      'Live score updates',
      'Track game stats',
      'Manage game clock',
      'Event coordination',
      'Technical support'
    ]
  },
  {
    id: 'scorekeeper',
    title: 'ScoreKeeper',
    description: 'Specialized role for managing game scores and statistics.',
    accountTier: 'free',
    features: [
      'Real-time score tracking',
      'Game statistics recording',
      'Team roster management',
      'Connect with teams',
      'Live game updates'
    ]
  },
  {
    id: 'fan',
    title: 'Fan',
    description: 'Follow, like, comment, and stay updated.',
    accountTier: 'free',
    features: [
      'Follow favorite teams',
      'Get game notifications',
      'Engage with content',
      'Connect with community'
    ]
  },
  {
    id: 'parent',
    title: 'Parent',
    description: 'Manage child accounts and provide consent.',
    accountTier: 'free',
    features: [
      'Manage child accounts',
      'Approve activities',
      'Team communication',
      'View family schedule'
    ]
  },
  {
    id: 'agent',
    title: 'Agent / NIL Tools',
    description: 'Professional tools for athlete discovery and NIL workflows.',
    accountTier: 'paid',
    features: [
      'Athlete discovery & search',
      'Messaging templates',
      'Deal workflow tools',
      'Calendar scheduling',
      'Analytics dashboard (Premium)'
    ]
  }
];

export const getFreeRoles = (): RoleData[] => {
  return roles.filter(role => role.accountTier === 'free');
};

export const getPaidRoles = (): RoleData[] => {
  return roles.filter(role => role.accountTier === 'paid');
};

export const getSignupRoles = (): RoleData[] => {
  const signupRoleIds: UserRole[] = ['player', 'coach', 'team-owner', 'venue', 'scorekeeper', 'fan', 'parent'];
  return roles.filter(role => signupRoleIds.includes(role.id));
};

export const getNonParentSignupRoles = (): RoleData[] => {
  const nonParentRoleIds: UserRole[] = ['player', 'coach', 'team-owner', 'venue', 'scorekeeper', 'fan'];
  return roles.filter(role => nonParentRoleIds.includes(role.id));
};
